import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import FileDelimitedIcon from 'mdi-react/FileDelimitedIcon'
import MicrosoftExcelIcon from 'mdi-react/MicrosoftExcelIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'

import { getISODate } from './helpers'

import { exportDataToExcel } from '../../helpers'
import type { ReportFilters } from '../../resources'
import adminResources from '../../resources'

export const NewNumberOrdersOptions: FC<{
  title: string
  filters: ReportFilters
}> = ({ title, filters }) => {
  const translate = useTranslation()
  const profile = useProfile()
  const isThreeStepIT = useIsThreeStepIT()

  const { data: reports } = useQuery(queries.adminNewNumberOrdersReports(filters), () =>
    adminResources.getNewNumberOrdersReports(filters)
  )

  if (!reports) throw Error('We have encountered a problem while loading reports')

  const reportsData = reports.map(report => ({
    name: report.simName,
    phoneNo: report.phoneNo,
    employeeNumber: report.employeeNumber,
    contactNumber: report.contactNumber,
    activationDate: getISODate(report.activationDate),
    departmentCode: report.department.code,
    department: report.department.name,
    salaryDeductionProfile: report.salaryDeductionProfile?.name,
    devicePolicy: report.devicePolicy?.name,
    currency: profile?.currency,
  }))
  const columnFormats = {
    activationDate: 'date',
  }

  return (
    <div className="grid grid-cols-2">
      <Button
        Icon={<MicrosoftExcelIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reportsData, title, {
            translate,
            columnFormats,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="Excel" />
      </Button>
      <Button
        Icon={<FileDelimitedIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reportsData, title, {
            translate,
            columnFormats,
            extension: 'csv',
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="CSV" />
      </Button>
    </div>
  )
}
